import {
  getModule,
  Module,
  Mutation,
  VuexModule,
  Action,
} from 'vuex-module-decorators';
import { isValidBbox } from '@/stores/grid/grid-model';
import store from '@/stores/grid/grid-store';
import { LatLngBounds } from 'leaflet';

@Module({
  dynamic: true,
  namespaced: true,
  name: 'GridModule',
  store,
})
class GridModule extends VuexModule {
  private _bboxSouth: string | null = null;
  private _bboxWest: string | null = null;
  private _bboxNorth: string | null = null;
  private _bboxEast: string | null = null;
  private _textualBbox: string | null = null;

  public get bbox(): Array<string | null> {
    return [this.bboxSouth, this.bboxWest, this.bboxNorth, this.bboxEast];
  }

  public get bboxSouth(): string | null {
    return this._bboxSouth;
  }

  public get bboxWest(): string | null {
    return this._bboxWest;
  }

  public get bboxNorth(): string | null {
    return this._bboxNorth;
  }

  public get bboxEast(): string | null {
    return this._bboxEast;
  }

  public get textualBbox(): string | null {
    return this._textualBbox;
  }

  public get isValidBbox() {
    return isValidBbox({
      south: this.bboxSouth,
      west: this.bboxWest,
      north: this.bboxNorth,
      east: this.bboxEast,
    });
  }

  public get leafletBbox(): LatLngBounds | null {
    if (
      !this.bboxSouth ||
      !this.bboxEast ||
      !this.bboxWest ||
      !this.bboxNorth
    ) {
      return null;
    }
    return new LatLngBounds(
      [parseFloat(this.bboxSouth), parseFloat(this.bboxWest)],
      [parseFloat(this.bboxNorth), parseFloat(this.bboxEast)]
    );
  }

  @Mutation
  public updateBboxSouth(south: string | null) {
    this._bboxSouth = south;
  }

  @Mutation
  public updateBboxWest(west: string | null) {
    this._bboxWest = west;
  }

  @Mutation
  public updateBboxNorth(north: string | null) {
    this._bboxNorth = north;
  }

  @Mutation
  public updateBboxEast(east: string | null) {
    this._bboxEast = east;
  }

  @Mutation
  public updateTextualBbox(textualBbox: string) {
    this._textualBbox = textualBbox;
  }

  @Action
  public changeBboxSouth(south: string | null) {
    this.updateBboxSouth(south);
    this.changeTextualBboxFromCoords();
  }

  @Action
  public changeBboxWest(west: string | null) {
    this.updateBboxWest(west);
    this.changeTextualBboxFromCoords();
  }

  @Action
  public changeBboxNorth(north: string | null) {
    this.updateBboxNorth(north);
    this.changeTextualBboxFromCoords();
  }

  @Action
  public changeBboxEast(east: string | null) {
    this.updateBboxEast(east);
    this.changeTextualBboxFromCoords();
  }

  @Action
  public updateBbox(bounds: LatLngBounds): void {
    this.updateBboxEast(bounds.getEast().toString());
    this.updateBboxNorth(bounds.getNorth().toString());
    this.updateBboxWest(bounds.getWest().toString());
    this.updateBboxSouth(bounds.getSouth().toString());
    this.changeTextualBboxFromCoords();
  }

  @Action
  public changeTextualBboxFromCoords() {
    this.updateTextualBbox(
      [this.bboxSouth, this.bboxWest, this.bboxNorth, this.bboxEast].join(',')
    );
  }

  @Action
  public changeTextualBbox(textualBbox: string) {
    this.updateTextualBbox(textualBbox);

    const arr = textualBbox.split(',');
    let south = null;
    let west = null;
    let north = null;
    let east = null;

    if (arr.length >= 1) {
      south = arr[0];
    }

    if (arr.length >= 2) {
      west = arr[1];
    }

    if (arr.length >= 3) {
      north = arr[2];
    }

    if (arr.length >= 4) {
      east = arr[3];
    }

    this.updateBboxSouth(south);
    this.updateBboxWest(west);
    this.updateBboxNorth(north);
    this.updateBboxEast(east);
  }

  @Action
  public resetBbox() {
    this.updateBboxSouth(null);
    this.updateBboxWest(null);
    this.updateBboxNorth(null);
    this.updateBboxEast(null);
    this.updateTextualBbox('');
  }
}

export default getModule(GridModule);
